import { SALUTATIONS } from '../../../../lib/salutations';
import { addDays, todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { toDateTimeLocal } from '../../../../lib/formatDate';
import { uid } from '../../../../lib/uid';
import {
  DISPATCH_MODE,
  DOCUMENT_MEDIUM,
  ROUTE_PURPOSE,
  type DispatchMode,
  type DocumentMedium,
  type PersonNameParts,
  type RoutePurpose,
  type RoutingDocumentTypeRequest,
} from '../routingTypes';

/**
 * Synthetic Routing records, for filling a form in one click.
 *
 * **The named sender and addressee LAND IN `rsys_pii`**, so they are OBVIOUSLY synthetic —
 * `Placeholder Testcase` — and never a plausible person: a fabricated-but-realistic name in that
 * store is indistinguishable from a real one to whoever audits it. Offices and subjects land in
 * `rsys_`, where plausible vocabulary is fine. Anything unique is SUFFIXED.
 */

/** A document type. The name is unique among live rows, so the second click must not collide. */
export function sampleRoutingDocumentType(): RoutingDocumentTypeRequest {
  const kind = pick(TYPES);

  return { name: `${kind.name} ${uid().slice(0, 6).toUpperCase()}`, description: kind.description };
}

/** The obviously synthetic individual — see the file's note. */
export function sampleCorrespondent(): PersonNameParts {
  return {
    salutation: SALUTATIONS[Math.floor(Math.random() * SALUTATIONS.length)],
    firstName: 'Placeholder',
    middleName: '',
    lastName: `Testcase ${uid().slice(0, 4).toUpperCase()}`,
    suffix: '',
  };
}

/**
 * A received document's form values, resolved against the LIVE catalogs the form loaded.
 *
 * **Both sides of the sender are filled** — a unit AND an outside organization with its named
 * person. Which one the form sends is the reader's choice of kind, which stays where it is: a
 * flag that reveals a block is left at its default, and what sits behind either answer is filled.
 */
export function sampleReceivedDocument(catalogs: { typeIds: string[]; unitIds: string[] }): {
  subject: string;
  documentTypeId: string;
  senderOrgUnitId: string;
  senderOffice: string;
  sender: PersonNameParts;
  senderReferenceNumber: string;
  documentDate: string;
  receivedAt: string;
  receivingOrgUnitId: string;
  remarks: string;
} {
  const receivingOrgUnitId = pickOrEmpty(catalogs.unitIds);

  return {
    subject: `${pick(SUBJECTS)} ${uid().slice(0, 4).toUpperCase()}`,
    documentTypeId: pickOrEmpty(catalogs.typeIds),
    // Never the receiving unit — a unit does not receive from itself.
    senderOrgUnitId: pickOrEmpty(catalogs.unitIds.filter((id) => id !== receivingOrgUnitId)),
    senderOffice: pick(OFFICES),
    sender: sampleCorrespondent(),
    senderReferenceNumber: `MEMO-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 900) + 100)}`,
    documentDate: addDays(todayCalendarDate(), -Math.floor(Math.random() * 7) - 1),

    // A little before now, so the server's not-in-the-future rule never refuses it.
    receivedAt: toDateTimeLocal(new Date(Date.now() - (Math.floor(Math.random() * 180) + 5) * 60_000)),
    receivingOrgUnitId,
    remarks: pick(REMARKS),
  };
}

/** A forward — any unit but the one holding the document. */
export function sampleRoute(unitIds: string[]): { toOrgUnitId: string; purpose: RoutePurpose; remarks: string } {
  return {
    toOrgUnitId: pickOrEmpty(unitIds),
    purpose: pick(Object.values(ROUTE_PURPOSE)),
    remarks: pick(ROUTE_REMARKS),
  };
}

/**
 * A dispatch's form values. The source is the form's own question and is left to it, and so is
 * the recipient's kind — both sides are filled, as on a received document. The unit is never the
 * one it leaves from.
 */
export function sampleDispatchedDocument(catalogs: { typeIds: string[]; unitIds: string[]; fromUnitId: string }): {
  subject: string;
  documentTypeId: string;
  medium: DocumentMedium;
  recipientOrgUnitId: string;
  recipientOffice: string;
  recipient: PersonNameParts;
  mode: DispatchMode;
  dispatchedAt: string;
  carrierReferenceNumber: string;
  remarks: string;
} {
  const mode = pick(Object.values(DISPATCH_MODE));

  return {
    subject: `${pick(REPLIES)} ${uid().slice(0, 4).toUpperCase()}`,
    documentTypeId: pickOrEmpty(catalogs.typeIds),
    medium: mode === DISPATCH_MODE.email ? DOCUMENT_MEDIUM.softCopy : DOCUMENT_MEDIUM.hardCopy,
    recipientOrgUnitId: pickOrEmpty(catalogs.unitIds.filter((id) => id !== catalogs.fromUnitId)),
    recipientOffice: pick(OFFICES),
    recipient: sampleCorrespondent(),
    mode,
    // NOW, to the minute — the server refuses a dispatch before the document's last movement.
    dispatchedAt: toDateTimeLocal(new Date()),
    carrierReferenceNumber: `WB-${String(Math.floor(Math.random() * 9_000_000) + 1_000_000)}`,
    remarks: pick(REMARKS),
  };
}

function pick<T>(values: readonly T[]): T {
  return values[Math.floor(Math.random() * values.length)];
}

function pickOrEmpty(values: string[]): string {
  return values.length > 0 ? pick(values) : '';
}

const TYPES = [
  { name: 'Letter', description: 'Correspondence from or to an outside office.' },
  { name: 'Memorandum', description: 'An internal or inter-agency memo.' },
  { name: 'Endorsement', description: 'A document passed on with a covering note.' },
  { name: 'Invoice', description: 'A bill from a supplier.' },
] as const;

const SUBJECTS = [
  'Request for budget figures',
  'Invitation to a coordination meeting',
  'Submission of quarterly report',
  'Follow-up on pending payment',
  'Notice of inspection',
] as const;

const REPLIES = ['Reply to request', 'Transmittal of report', 'Notice of meeting', 'Endorsement of request'] as const;

const OFFICES = [
  'Department of Budget and Management',
  'Commission on Audit — Regional Office',
  'City Treasurer’s Office',
  'Sample Supplier Trading',
  'Provincial Planning Office',
] as const;

const REMARKS = [
  'Original kept at the receiving desk.',
  'Two copies received; one forwarded.',
  'Marked urgent by the sender.',
  'Attachments listed on the cover page.',
] as const;

const ROUTE_REMARKS = [
  'Please review and advise.',
  'For your signature before release.',
  'For filing in the unit records.',
  'Kindly act within three working days.',
] as const;
